'use client';

import { c as _c } from "react/compiler-runtime";
import { environmentVariables } from '@/config/environment-variables';
import { useAppMode } from '@/hooks/use-app-mode';
import { performReliablePushNavigation } from '@/hooks/use-native-push';
import { usePushSubscriptionKeyRenewal } from '@/hooks/use-push-subscription-key-renewal';
import { useServiceWorkerClientUrlResponder } from '@/hooks/use-service-worker-client-url-responder';
import { useServiceWorkerMessage } from '@/hooks/use-service-worker-message';
import { useServiceWorkerRegistration } from '@/hooks/use-service-worker-registration';
import { SerwistProvider, useSerwist } from '@/lib/serwist-client';
import { useOptionalTrpcUtils } from '@/trpc/client';
import { refreshAndOptimisticallyUpdateChat } from '@/utils/push-query-refresher';
import { ServiceWorkerMessages } from '@/utils/service-worker-messages';
import { SW_REGISTRATION_OPTIONS, SW_URL } from '@/utils/service-worker-utils';
import { useRouter } from 'next/navigation';
import type { ReactNode } from 'react';
import React from 'react';
interface ServiceWorkerManagerProperties {
  children: ReactNode;
}

/**
 * Registers the service worker created by {@link SerwistProvider}.
 *
 * `SerwistProvider` would register it itself, but it does so without handling a rejected
 * registration, which turns a browser that cannot install a service worker into an
 * unhandled rejection on every page load. The provider registers with the default scope
 * `/`, so its own out-of-scope check never applies here.
 */
const ServiceWorkerRegistration: React.FC = () => {
  const $ = _c(1);
  const {
    serwist
  } = useSerwist();
  useServiceWorkerRegistration(serwist);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <></>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};

/**
 * A central component to manage Service Worker registration and App Mode detection.
 * This ensures that the SW is registered consistently across different layouts.
 */
export const ServiceWorkerManager: React.FC<ServiceWorkerManagerProperties> = t0 => {
  const $ = _c(9);
  const {
    children
  } = t0;
  useAppMode();
  const router = useRouter();
  const trpcUtils = useOptionalTrpcUtils();
  useServiceWorkerClientUrlResponder();
  usePushSubscriptionKeyRenewal();
  let t1;
  if ($[0] !== router || $[1] !== trpcUtils) {
    t1 = payload => {
      if (typeof payload.url === "string" && payload.url.trim() !== "") {
        const rawUrl = payload.url.trim();
        let targetPath = "/app/dashboard";
        if (rawUrl.startsWith("/") && !rawUrl.startsWith("//")) {
          targetPath = rawUrl;
        } else {
          try {
            const parsedUrl = new URL(rawUrl, globalThis.location.origin);
            targetPath = parsedUrl.pathname + parsedUrl.search;
          } catch {
            console.warn("[Service Worker Manager] Could not parse navigation URL:", rawUrl);
          }
        }
        let targetChatId;
        if (targetPath.includes("/app/chat/")) {
          const parts = targetPath.split("/app/chat/");
          const firstPart = parts[1];
          if (typeof firstPart === "string" && firstPart !== "") {
            targetChatId = firstPart.split("?")[0];
          }
        }
        refreshAndOptimisticallyUpdateChat(trpcUtils, targetChatId, payload.payload);
        console.log("[Service Worker Manager] PUSH_NAVIGATE received, navigating to:", targetPath);
        performReliablePushNavigation(router, targetPath);
      }
    };
    $[0] = router;
    $[1] = trpcUtils;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  useServiceWorkerMessage(ServiceWorkerMessages.PUSH_NAVIGATE, t1);
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  React.useEffect(_temp3, t2);
  if (environmentVariables.NEXT_PUBLIC_DISABLE_SERWIST) {
    let t3;
    if ($[4] !== children) {
      t3 = <>{children}</>;
      $[4] = children;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    return t3;
  }
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <ServiceWorkerRegistration />;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== children) {
    t4 = <SerwistProvider register={false} reloadOnOnline={false} swUrl={SW_URL} options={SW_REGISTRATION_OPTIONS}>{t3}{children}</SerwistProvider>;
    $[7] = children;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
};
function _temp(registrations) {
  let unregisteredAny = false;
  const unregisterPromises = registrations.map(registration => registration.unregister().then(success => {
    if (success) {
      console.log("[Service Worker Manager] Unregistered active service worker:", registration.scope);
      unregisteredAny = true;
    }
  }));
  Promise.all(unregisterPromises).then(() => {
    if (unregisteredAny) {
      console.log("[Service Worker Manager] Reloading page to clear stale cache.");
      globalThis.location.reload();
    }
  }).catch(console.error);
}
function _temp2(error) {
  return console.error("[Service Worker Manager] Failed to unregister service workers:", error);
}
function _temp3() {
  if (environmentVariables.NEXT_PUBLIC_DISABLE_SERWIST && typeof navigator !== "undefined" && "serviceWorker" in navigator) {
    navigator.serviceWorker.getRegistrations().then(_temp).catch(_temp2);
  }
}